import {
    SetStateAction,
    Dispatch,
    PropsWithChildren,
    FC,
    useEffect,
    useState,
} from 'react'
import { Table } from 'antd'
import { SorterResult } from 'antd/lib/table/interface'
import styles from '@OpsQuality/styles.css'
import { useSelector } from 'react-redux'
import { ResizeCallbackData } from 'react-resizable'
import {
    handleColumnResize,
    getResizeableTableColumns,
    ResizeableTableHeader,
} from '@OpsWb/components/ResizeableTableHeader'
import { OPS_WB_TABLES } from '@OpsWb/constants'
import { useSavePreferences, useUrlFilters } from '@OpsWb/logic'
import { MP_DETAILS_TABS, PREFERENCES_PROPERTIES } from '@MfgPartner/constants'

import { JOB_REQUEST_LIST_PAGINATION_LIMIT } from '../constants'
import Box from '~/core/Box'
import Button from '~/core/Button'
import colors from '~/styles/_colors.css'
import { getUserViewPreferences } from '~/redux-modules/auth'
import { PreferenceType } from '~/gql/graphql'

import { getAdminJobRequestsQuality_adminJobRequests_items as JobRequestType } from './__generated__/getAdminJobRequestsQuality'
import { getJobsRequestListTableColumns } from './JobsRequestColumnList'

const PLACEMENT = 'quality-jobs-list'

interface ListTableProps {
    items: JobRequestType[]
    loading: boolean
    onPageChange: Dispatch<SetStateAction<number>>
    onSorterChange: (
        sorter: SorterResult<JobRequestType> | SorterResult<JobRequestType>[],
    ) => void
    pageNumber: number
    totalCount: number
    paginationInfo: any
}

const JobRequestListTable: FC<PropsWithChildren<ListTableProps>> = props => {
    const {
        onSorterChange,
        items,
        loading,
        pageNumber,
        onPageChange,
        totalCount,
    } = props

    const userPreferences = useSelector(getUserViewPreferences)?.preferences
        ?.tableViewPreferences?.[OPS_WB_TABLES.qualityJobRequestList]

    const { filtersObject } = useUrlFilters({
        currentTab: MP_DETAILS_TABS.JOBS,
    })

    const [saving, setSaving] = useState(false)
    const [prefsChanged, setPrefsChanged] = useState(false)
    const [columnsState, setColumnsState] = useState(
        getJobsRequestListTableColumns({
            userPreferences,
            appliedFilters: filtersObject,
        }),
    )

    useEffect(() => {
        setColumnsState(
            getJobsRequestListTableColumns({
                userPreferences,
                appliedFilters: filtersObject,
            }),
        )
    }, [filtersObject, userPreferences])

    const saveUserPreferences = useSavePreferences()

    const resizeHandler = (index: number, data: ResizeCallbackData) => {
        setPrefsChanged(true)
        handleColumnResize(setColumnsState, index, data)
    }

    const columns = getResizeableTableColumns<JobRequestType>(
        columnsState,
        resizeHandler,
    )

    const components = {
        header: {
            cell: ResizeableTableHeader,
        },
    }

    const savePreferences = () => {
        setSaving(true)
        saveUserPreferences<JobRequestType>({
            columnsObject: columnsState,
            properties: PREFERENCES_PROPERTIES,
            preferenceType: PreferenceType.tableViewPreferences,
            name: OPS_WB_TABLES.qualityJobRequestList,
        })
        setSaving(false)
        setPrefsChanged(false)
    }

    return (
        <Box direction="col" backgroundColor={colors.white}>
            <Table
                {...(prefsChanged && {
                    title: () => (
                        <Box justifyContent="end" alignItems="center">
                            <Button
                                size="small"
                                name="save-preferences-btn"
                                placement={PLACEMENT}
                                data-test-target="save-quality-job-requests-preferences"
                                onClick={savePreferences}
                                loading={saving}
                            >
                                Save column widths
                            </Button>
                        </Box>
                    ),
                })}
                className={styles.qcJobRequestTable}
                loading={loading}
                columns={columns}
                pagination={{
                    position: ['bottomRight'],
                    current: pageNumber,
                    total: totalCount || 0,
                    defaultPageSize: JOB_REQUEST_LIST_PAGINATION_LIMIT,
                    onChange: newPageNumber => onPageChange(newPageNumber),
                    showSizeChanger: false,
                }}
                onChange={(pagination, filters, sorter) => {
                    onSorterChange(sorter)
                }}
                components={components}
                scroll={{ x: 1600, y: '60vh' }}
                bordered
                dataSource={items || []}
            />
        </Box>
    )
}

export default JobRequestListTable
